/**
 * Hiring Plan Template Form
 * Recruitment planning and tracking
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const HiringPlanForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    department: '',
    fiscal_year: new Date().getFullYear().toString(),
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    approved_by: '',
    total_headcount_budget: 0,
    current_headcount: 0,
    planned_hires: 0,
    positions: [{
      position_id: 'POS-001', title: '', department: '', level: '', employment_type: 'Full-time',
      location: '', remote: 'Hybrid', salary_range: '', required_skills: '', nice_to_have: '',
      justification: '', hiring_manager: '', recruiter: '', priority: 'Medium', status: 'Draft',
      target_start_date: '', posted_date: '', candidates_count: 0, interview_stage: '', notes: ''
    }],
    budget_allocated: 0,
    budget_used: 0,
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    department: 'Engineering',
    fiscal_year: '2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'HR Director',
    approved_by: 'CFO',
    total_headcount_budget: 20,
    current_headcount: 45,
    planned_hires: 12,
    positions: [
      { position_id: 'ENG-001', title: 'Senior Software Engineer', department: 'Engineering', level: 'Senior', employment_type: 'Full-time', location: 'San Francisco, CA', remote: 'Hybrid', salary_range: '$150,000 - $180,000', required_skills: 'Python, AWS, Kubernetes, 5+ years experience', nice_to_have: 'ML/AI experience, distributed systems', justification: 'Platform V2 development requires additional senior capacity', hiring_manager: 'John Smith', recruiter: 'Jane HR', priority: 'Critical', status: 'Interviewing', target_start_date: '2025-02-01', posted_date: '2024-12-15', candidates_count: 8, interview_stage: 'Technical Round', notes: '3 candidates in final round' },
      { position_id: 'ENG-002', title: 'DevOps Engineer', department: 'Engineering', level: 'Mid', employment_type: 'Full-time', location: 'Remote', remote: 'Remote', salary_range: '$120,000 - $145,000', required_skills: 'Terraform, AWS, CI/CD, Kubernetes', nice_to_have: 'Security certifications', justification: 'Infrastructure scaling and reliability improvements', hiring_manager: 'Mike Wilson', recruiter: 'Jane HR', priority: 'High', status: 'Open', target_start_date: '2025-03-01', posted_date: '2025-01-05', candidates_count: 12, interview_stage: 'Screening', notes: '' },
      { position_id: 'ENG-003', title: 'Frontend Developer', department: 'Engineering', level: 'Mid', employment_type: 'Full-time', location: 'New York, NY', remote: 'Hybrid', salary_range: '$110,000 - $135,000', required_skills: 'React, TypeScript, CSS, 3+ years', nice_to_have: 'Mobile development (React Native)', justification: 'Mobile app v2 redesign project', hiring_manager: 'Sarah Chen', recruiter: 'Jane HR', priority: 'Medium', status: 'Draft', target_start_date: '2025-04-01', posted_date: '', candidates_count: 0, interview_stage: '', notes: 'Pending budget approval' },
      { position_id: 'QA-001', title: 'QA Automation Engineer', department: 'QA', level: 'Senior', employment_type: 'Full-time', location: 'Remote', remote: 'Remote', salary_range: '$125,000 - $150,000', required_skills: 'Selenium, Cypress, Python, CI/CD integration', nice_to_have: 'Performance testing experience', justification: 'Improve test coverage and automation for Platform V2', hiring_manager: 'QA Manager', recruiter: 'Jane HR', priority: 'High', status: 'Open', target_start_date: '2025-02-15', posted_date: '2024-12-20', candidates_count: 5, interview_stage: 'Technical Assessment', notes: '' },
      { position_id: 'PM-001', title: 'Product Manager', department: 'Product', level: 'Senior', employment_type: 'Full-time', location: 'San Francisco, CA', remote: 'Hybrid', salary_range: '$160,000 - $190,000', required_skills: 'B2B SaaS experience, 5+ years PM experience, technical background', nice_to_have: 'Enterprise sales experience', justification: 'New product line expansion', hiring_manager: 'VP Product', recruiter: 'External Agency', priority: 'Medium', status: 'Sourcing', target_start_date: '2025-05-01', posted_date: '', candidates_count: 0, interview_stage: '', notes: 'Using executive recruiter' }
    ],
    budget_allocated: 1800000,
    budget_used: 450000,
    rai_assignments: [
      { task: 'Finalize Q1 hiring priorities', responsible: 'HR Director', accountable: 'CFO', informed: 'Department Heads', due_date: '2025-01-15', status: 'In Progress' },
      { task: 'Review salary bands for 2025', responsible: 'Compensation Manager', accountable: 'HR Director', informed: 'Finance, Legal', due_date: '2025-01-31', status: 'Pending' }
    ],
    followups: [
      { item: 'Schedule final interviews for ENG-001', owner: 'Jane HR', due_date: '2025-01-18', priority: 'Critical', status: 'In Progress' },
      { item: 'Post DevOps role on LinkedIn', owner: 'Recruiter', due_date: '2025-01-10', priority: 'High', status: 'Completed' },
      { item: 'Get budget approval for Frontend role', owner: 'HR Director', due_date: '2025-01-25', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Hiring Plan Draft";
      const payload = {
        name: draftName,
        template_type: "Hiring Plan",
        template_id: "hiring-plan",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "hiring-plan", "Hiring Plan", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("hiring-plan", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/hiring-plan`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Hiring_Plan_${formData.company_name.replace(/\s+/g, '_') || 'Company'}_${formData.fiscal_year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Hiring Plan",
        formData,
        settings: activeSettings || {},
        templateType: "Hiring Plan",
        templateId: "hiring-plan",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addPosition = () => {
    const newId = `POS-${String(formData.positions.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      positions: [...formData.positions, {
        position_id: newId, title: '', department: formData.department, level: '', employment_type: 'Full-time',
        location: '', remote: 'Hybrid', salary_range: '', required_skills: '', nice_to_have: '',
        justification: '', hiring_manager: '', recruiter: '', priority: 'Medium', status: 'Draft',
        target_start_date: '', posted_date: '', candidates_count: 0, interview_stage: '', notes: ''
      }]
    });
  };

  const updatePosition = (index, field, value) => {
    const positions = [...formData.positions];
    positions[index] = { ...positions[index], [field]: value };
    setFormData({ ...formData, positions });
  };

  const removePosition = (index) => {
    setFormData({ ...formData, positions: formData.positions.filter((_, i) => i !== index) });
  };

  const levels = ['Entry', 'Junior', 'Mid', 'Senior', 'Lead', 'Principal', 'Director', 'VP', 'C-Level'];
  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Intern'];
  const remoteOptions = ['On-site', 'Hybrid', 'Remote'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Draft', 'Pending Approval', 'Open', 'Sourcing', 'Interviewing', 'Offer Extended', 'Filled', 'On Hold', 'Cancelled'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Hiring Plan</h1>
                <p className="text-sm text-gray-500">Recruitment planning and tracking</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Hiring Plan Overview</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="All / Engineering" /></div>
              <div><Label>Fiscal Year</Label><Input value={formData.fiscal_year} onChange={(e) => setFormData({...formData, fiscal_year: e.target.value})} placeholder="2025" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="HR Director" /></div>
              <div><Label>Approved By</Label><Input value={formData.approved_by} onChange={(e) => setFormData({...formData, approved_by: e.target.value})} placeholder="CFO" /></div>
              <div><Label>Current Headcount</Label><Input type="number" value={formData.current_headcount} onChange={(e) => setFormData({...formData, current_headcount: parseInt(e.target.value) || 0})} /></div>
              <div><Label>Planned Hires</Label><Input type="number" value={formData.planned_hires} onChange={(e) => setFormData({...formData, planned_hires: parseInt(e.target.value) || 0})} /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Open Positions ({formData.positions.length})</Label>
              <div className="space-y-4 mt-3">
                {formData.positions.map((pos, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">{pos.position_id}</span>
                        <span className={`text-xs px-2 py-1 rounded ${pos.priority === 'Critical' ? 'bg-red-100 text-red-700' : pos.priority === 'High' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100'}`}>{pos.priority}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removePosition(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="col-span-2"><Label className="text-xs">Job Title *</Label><Input value={pos.title} onChange={(e) => updatePosition(idx, 'title', e.target.value)} placeholder="Senior Software Engineer" className="text-sm" /></div>
                      <div><Label className="text-xs">Department</Label><Input value={pos.department} onChange={(e) => updatePosition(idx, 'department', e.target.value)} placeholder="Engineering" className="text-sm" /></div>
                      <div><Label className="text-xs">Level</Label>
                        <Select value={pos.level} onValueChange={(v) => updatePosition(idx, 'level', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                      <div><Label className="text-xs">Employment Type</Label>
                        <Select value={pos.employment_type} onValueChange={(v) => updatePosition(idx, 'employment_type', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{employmentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Location</Label><Input value={pos.location} onChange={(e) => updatePosition(idx, 'location', e.target.value)} placeholder="City, State" className="text-sm" /></div>
                      <div><Label className="text-xs">Remote</Label>
                        <Select value={pos.remote} onValueChange={(v) => updatePosition(idx, 'remote', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{remoteOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Salary Range</Label><Input value={pos.salary_range} onChange={(e) => updatePosition(idx, 'salary_range', e.target.value)} placeholder="$X - $Y" className="text-sm" /></div>
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={pos.priority} onValueChange={(v) => updatePosition(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div><Label className="text-xs">Required Skills</Label><Input value={pos.required_skills} onChange={(e) => updatePosition(idx, 'required_skills', e.target.value)} placeholder="Python, AWS, 5+ years" className="text-sm" /></div>
                      <div><Label className="text-xs">Nice to Have</Label><Input value={pos.nice_to_have} onChange={(e) => updatePosition(idx, 'nice_to_have', e.target.value)} placeholder="ML experience" className="text-sm" /></div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                      <div><Label className="text-xs">Hiring Manager</Label><Input value={pos.hiring_manager} onChange={(e) => updatePosition(idx, 'hiring_manager', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Recruiter</Label><Input value={pos.recruiter} onChange={(e) => updatePosition(idx, 'recruiter', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={pos.status} onValueChange={(v) => updatePosition(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Target Start</Label><Input type="date" value={pos.target_start_date} onChange={(e) => updatePosition(idx, 'target_start_date', e.target.value)} className="text-sm" /></div>
                      <div><Label className="text-xs">Candidates</Label><Input type="number" value={pos.candidates_count} onChange={(e) => updatePosition(idx, 'candidates_count', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                    </div>

                    <div className="mt-3">
                      <Label className="text-xs">Justification</Label>
                      <Textarea value={pos.justification} onChange={(e) => updatePosition(idx, 'justification', e.target.value)} placeholder="Business justification for this hire..." rows={2} className="text-sm" />
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addPosition} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Position</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default HiringPlanForm;
